import type { ThemeSeed } from "./seeds"

import { oklch } from "./utils"

export type ThemeTokens = Record<string, string>

export function deriveTokens(seed: ThemeSeed): ThemeTokens {
  const { base, primary, accent } = seed
  const isDark = seed.mode === "dark"

  const fg = isDark
    ? oklch(0.95, 0.01, primary.h + 6)
    : oklch(0.255, 0.035, base.h)
  const fgMuted = isDark
    ? oklch(0.8, 0.015, primary.h + 6)
    : oklch(0.49, 0.025, base.h)

  const cardL = base.l + 0.05
  const cardC = base.c * 0.8
  const cardAlpha = isDark ? 0.9 : 0.88

  const mutedL = base.l + 0.09
  const mutedC = base.c * 0.8
  const mutedH = base.h - 4

  const secondaryL = base.l + 0.145
  const secondaryH = base.h - 10

  const borderMidL = 0.66
  const borderC = 0.015
  const borderH = primary.h + 6
  const borderAlpha = isDark ? 0.22 : 0.55

  const surfaceBorderL = isDark ? 0.68 : 0.8
  const surfaceBorderC = 0.018
  const surfaceBorderAlpha = isDark ? 0.24 : 0.58

  const primaryFg = isDark
    ? oklch(base.l + 0.005, base.c * 0.8, base.h)
    : oklch(0.985, 0.003, primary.h)
  const accentFg = isDark
    ? oklch(base.l * 0.83, base.c * 0.4, base.h)
    : oklch(0.22, 0.02, base.h)

  const statusL = isDark ? 0.65 : 0.59
  const statusFgL = isDark ? 0.15 : 0.98
  const dangerL = isDark ? 0.704 : 0.577

  const ringL = isDark ? primary.l * 0.91 : 0.62
  const ringC = primary.c * 0.67
  const ringAlpha = isDark ? 0.7 : 0.65

  const [ch1, ch2, ch3, ch4, ch5] = [
    wrapHue(primary.h + 186),
    wrapHue(primary.h + 84),
    wrapHue(accent.h - 2),
    wrapHue(accent.h + 232),
    wrapHue(primary.h - 62),
  ]

  return {
    "--background": oklch(base.l, base.c, base.h),
    "--foreground": fg,
    "--card": oklch(cardL, cardC, base.h, cardAlpha),
    "--card-foreground": fg,
    "--popover": oklch(cardL, cardC, base.h),
    "--popover-foreground": fg,
    "--primary": oklch(primary.l, primary.c, primary.h),
    "--primary-foreground": primaryFg,
    "--secondary": oklch(secondaryL, base.c, secondaryH),
    "--secondary-foreground": fg,
    "--muted": oklch(mutedL, mutedC, mutedH),
    "--muted-foreground": fgMuted,
    "--accent": oklch(accent.l, accent.c, accent.h),
    "--accent-foreground": accentFg,
    "--destructive": oklch(dangerL, 0.191, 22.216),
    "--border": oklch(borderMidL, borderC, borderH, borderAlpha),
    "--input": oklch(borderMidL, borderC, borderH, borderAlpha),
    "--ring": oklch(ringL, ringC, primary.h, ringAlpha),

    "--success": oklch(statusL, 0.18, 145),
    "--success-foreground": oklch(statusFgL, 0.03, 145),
    "--warning": oklch(isDark ? 0.78 : 0.75, 0.14, 70),
    "--warning-foreground": oklch(isDark ? 0.2 : 0.25, 0.04, 70),
    "--danger": oklch(dangerL, 0.191, 22.216),
    "--danger-foreground": oklch(statusFgL, 0.02, 27),
    "--info": oklch(statusL, 0.16, 245),
    "--info-foreground": oklch(statusFgL, 0.02, 245),

    "--surface-1": oklch(base.l + 0.05, cardC, base.h, 0.92),
    "--surface-2": oklch(base.l + 0.08, cardC, base.h - 2, 0.94),
    "--surface-3": oklch(base.l + 0.12, base.c * 0.96, base.h - 8, 0.96),
    "--surface-border": oklch(
      surfaceBorderL,
      surfaceBorderC,
      borderH,
      surfaceBorderAlpha,
    ),
    "--surface-ring": oklch(1, 0, 0, isDark ? 0.08 : 0.55),

    "--chart-1": oklch(0.488, 0.243, ch1),
    "--chart-2": oklch(0.696, 0.17, ch2),
    "--chart-3": oklch(0.769, 0.188, ch3),
    "--chart-4": oklch(0.627, 0.265, ch4),
    "--chart-5": oklch(0.645, 0.246, ch5),

    "--sidebar": oklch(base.l, 0, 0),
    "--sidebar-foreground": oklch(0.985, 0, 0),
    "--sidebar-primary": oklch(0.488, 0.243, ch1),
    "--sidebar-primary-foreground": oklch(0.985, 0, 0),
    "--sidebar-accent": oklch(base.l + 0.064, 0, 0),
    "--sidebar-accent-foreground": oklch(0.985, 0, 0),
    "--sidebar-border": oklch(1, 0, 0, 0.1),
    "--sidebar-ring": oklch(0.556, 0, 0),
  }
}

export function wrapHue(h: number): number {
  return ((h % 360) + 360) % 360
}
